import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FileDown, RotateCcw, Save } from 'lucide-react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { fetchInvoiceTemplate, InvoicePreview, saveInvoiceTemplate } from '../components/TableInvoice';
import { buildTableInvoice, DEFAULT_INVOICE_TEMPLATE, downloadInvoicePDF } from '../utils/invoiceUtils';

// Sample table usage so the preview shows how a real bill will look
const SAMPLE_TABLE = {
    tableName: 'Table 1',
    tableNumber: 'T-001',
    gameType: 'Texas Holdem',
    club: { name: 'Sample Club', gstNumber: '27ABCDE1234F1Z5' },
    location: 'Main Hall',
    hourlyRate: 100,
};
const SAMPLE_RUNS = [
    {
        startedAt: moment().subtract(5, 'hours').toDate(),
        endedAt: moment().subtract(3, 'hours').subtract(45, 'minutes').toDate(),
        activeMinutes: 75,
        totalCharge: 200,
    },
    {
        startedAt: moment().subtract(3, 'hours').toDate(),
        endedAt: moment().subtract(30, 'minutes').toDate(),
        activeMinutes: 150,
        totalCharge: 500,
    },
];

const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {title}
        </Typography>
        <Grid container spacing={2}>
            {children}
        </Grid>
    </Box>
);

const InvoiceTemplateMaster = () => {
    const adminLogin = useSelector((state) => state.adminLogin) || {};
    const userLogin = useSelector((state) => state.userLogin) || {};
    const userInfo = adminLogin.userInfo || userLogin.userInfo;
    const token = userInfo?.token;
    const role = userInfo?.role || {};
    const canEdit = role.isSuperAdmin === true || role.invoiceTemplateManage === true;

    const [form, setForm] = useState(DEFAULT_INVOICE_TEMPLATE);
    const [saved, setSaved] = useState(DEFAULT_INVOICE_TEMPLATE);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!token) return;
        fetchInvoiceTemplate(token)
            .then((data) => {
                const loaded = { ...DEFAULT_INVOICE_TEMPLATE, ...data };
                setForm(loaded);
                setSaved(loaded);
            })
            .catch((error) => toast.error(error.response?.data?.message || 'Failed to load invoice template'))
            .finally(() => setLoading(false));
    }, [token]);

    const dirty = useMemo(
        () => Object.keys(DEFAULT_INVOICE_TEMPLATE).some((key) => form[key] !== saved[key]),
        [form, saved]
    );

    const previewInvoice = useMemo(
        () =>
            buildTableInvoice({
                table: SAMPLE_TABLE,
                runs: SAMPLE_RUNS,
                template: form,
                billTo: 'Sample Player',
            }),
        [form]
    );

    const setField = (key) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        const rate = Number(form.gstRate);
        if (form.gstRate === '' || !Number.isFinite(rate) || rate < 0 || rate > 100) {
            toast.error('GST rate must be between 0 and 100');
            return;
        }
        setSaving(true);
        try {
            const payload = Object.keys(DEFAULT_INVOICE_TEMPLATE).reduce(
                (acc, key) => ({ ...acc, [key]: form[key] }),
                {}
            );
            const data = await saveInvoiceTemplate(token, { ...payload, gstRate: rate });
            const updated = { ...DEFAULT_INVOICE_TEMPLATE, ...data };
            setForm(updated);
            setSaved(updated);
            toast.success('Invoice template saved');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save invoice template');
        } finally {
            setSaving(false);
        }
    };

    const field = (key, label, props = {}) => (
        <TextField
            size="small"
            fullWidth
            label={label}
            value={form[key] ?? ''}
            onChange={setField(key)}
            disabled={!canEdit}
            {...props}
        />
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        Invoice Template
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Master details printed on table usage bills from the Player & Table Report.
                    </Typography>
                </Box>
                <Box sx={{ ml: { md: 'auto' }, display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RotateCcw size={16} />}
                        disabled={!dirty || saving}
                        onClick={() => setForm(saved)}
                    >
                        Discard
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                        disabled={!canEdit || !dirty || saving}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </Box>
            </Box>

            {!canEdit && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    You can view this template but need the "Invoice Template" permission to edit it.
                </Typography>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} lg={5}>
                    <Card variant="outlined">
                        <CardContent>
                            <Section title="Business details">
                                <Grid item xs={12}>
                                    {field('businessName', 'Business name')}
                                </Grid>
                                <Grid item xs={12}>
                                    {field('businessAddress', 'Address', { multiline: true, minRows: 2 })}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('phone', 'Phone')}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('email', 'Email')}
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Default details. A club's own business details and GSTIN (Venues &gt; Edit Club) take priority on its invoices.
                                    </Typography>
                                </Grid>
                            </Section>

                            <Section title="Invoice header">
                                <Grid item xs={12} sm={7}>
                                    {field('invoiceTitle', 'Invoice title')}
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    {field('invoicePrefix', 'Invoice no. prefix')}
                                </Grid>
                            </Section>

                            <Section title="Line item">
                                <Grid item xs={12}>
                                    {field('itemDescription', 'Description')}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('sacCode', 'SAC / HSN code')}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('unitLabel', 'Unit usage label', { helperText: 'e.g. Hours' })}
                                </Grid>
                            </Section>

                            <Section title="Charges & tax">
                                <Grid item xs={12} sm={6}>
                                    {field('chargesLabel', 'Charges label')}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('gstLabel', 'GST label')}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('gstRate', 'GST rate', {
                                        type: 'number',
                                        inputProps: { min: 0, max: 100, step: 0.5 },
                                        InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                                    })}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('totalLabel', 'Total label')}
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(form.splitGst)}
                                                onChange={setField('splitGst')}
                                                disabled={!canEdit}
                                            />
                                        }
                                        label="Split GST into CGST + SGST"
                                    />
                                </Grid>
                            </Section>

                            <Section title="Footer">
                                <Grid item xs={12}>
                                    {field('notes', 'Notes / terms', { multiline: true, minRows: 2 })}
                                </Grid>
                                <Grid item xs={12}>
                                    {field('footerText', 'Footer text')}
                                </Grid>
                            </Section>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Preview (sample data)
                            </Typography>
                            <Button
                                size="small"
                                sx={{ ml: 'auto' }}
                                startIcon={<FileDown size={16} />}
                                onClick={() => downloadInvoicePDF(previewInvoice)}
                            >
                                Sample PDF
                            </Button>
                        </Box>
                        <InvoicePreview invoice={previewInvoice} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default InvoiceTemplateMaster;
